import { Definition, KpkApiSelectionAvailableExportGroupBy } from "@keepeek/api-client";
import {
  currentUserQuery,
  customerCommonOverrideAtom,
  customSelectorInOut,
  downloadManagerOpenState,
  downloadManagerPreferenceFormatFavoritesState,
  downloadManagerSelectedElementsState,
  fetcherModeAtom,
  FormatType,
  FunctionOverrideKey,
  RICH_TYPE_META_MODEL_ID,
} from "@keepeek/commons";
import { DownloadManagerFormatsByType } from "@keepeek/refront-components";
import sortBy from "lodash/sortBy";
import { selector } from "recoil";

import { DownloadManagerFormatsByElement } from "../../../containers/DownloadManager/TypeByElement";
import logger from "../../../lib/logger-utils";
import { downloadManagerFetcherSelector } from "../../fetcher/selectors/downloadManager";
import {
  downloadManagerSelectedFormatsByElementState,
  downloadManagerSelectedFormatsByTypeState,
} from "../atoms/formats";
import {
  convertToKpkFormatsByType,
  formatTypeAskDownload,
  formatTypeIdAskDownload,
} from "../utils";
import { requestDownloadIsEnableSelector } from "./askDownloads";

export const downloadManagerFormatsQuery = selector<{
  formatsByTypes: DownloadManagerFormatsByType[];
  selectionId: string | null;
}>({
  key: "DownloadManagerFormatsQuery",
  get: async ({ get }) => {
    // Override IN
    const { elementIds, userId } = customSelectorInOut(
      FunctionOverrideKey.DownloadManagerFormatsQueryIn,
      {
        elementIds: get(downloadManagerSelectedElementsState),
        userId: get(currentUserQuery).id,
      },
      get(customerCommonOverrideAtom),
    );

    // No userId check at this point because it is not mandatory for public share
    if (elementIds.length > 0) {
      const { postSelection, postSelectionMediasCollection, getSelectionAvailableExportFormats } =
        get(downloadManagerFetcherSelector(get(fetcherModeAtom)));

      try {
        // Create a selection
        const postSelectionQuery = await postSelection({
          userId,
        });
        if (!postSelectionQuery) {
          throw new Error("cannot post selection without authenticated user");
        }
        const {
          data: { id: selectionIdOut },
        } = postSelectionQuery;
        // Add element(s) to the selection
        await postSelectionMediasCollection({
          userId,
          selectionId: selectionIdOut,
          mediaIds: elementIds,
        });
        // Get formats from the selection
        const getSelectionAvailableExportFormatsQuery = await getSelectionAvailableExportFormats({
          userId,
          selectionId: selectionIdOut,
          groupBy: KpkApiSelectionAvailableExportGroupBy.MediaType,
        });
        if (!getSelectionAvailableExportFormatsQuery) {
          throw new Error("cannot get selection without authenticated user");
        }
        const {
          data: { _embedded },
        } = getSelectionAvailableExportFormatsQuery;
        // Convert to UI readable
        if (_embedded?.["available-export-format"]) {
          const formatsByTypesUnsorted = convertToKpkFormatsByType(
            _embedded["available-export-format"],
          );
          // Sort by number of element by type
          const formatsByTypesOut = sortBy(
            // Then sort by number of formats if it's the same number of element
            sortBy(formatsByTypesUnsorted, [(formatsByType) => formatsByType.formats.length]),
            [(formatsByType) => formatsByType.elements.length],
          ).reverse();

          // Override OUT
          const { formatsByTypes, selectionId } = customSelectorInOut(
            FunctionOverrideKey.DownloadManagerFormatsQueryOut,
            {
              formatsByTypes: formatsByTypesOut,
              selectionId: selectionIdOut,
            },
            get(customerCommonOverrideAtom),
          );

          return {
            formatsByTypes,
            selectionId,
          };
        }
        logger.info("downloadManagerFormatsQuery: no element in selection");
        return {
          formatsByTypes: [],
          selectionId: null,
        };
      } catch (error) {
        logger.error(`downloadManagerFormatsQuery: ${error}`);
        // TODO: Error handling
      }
    }

    return {
      formatsByTypes: [],
      selectionId: null,
    };
  },
});

export const downloadManagerMergeSelectedFormatWithTypeSelector = selector<{
  formatsByTypes: DownloadManagerFormatsByType[];
  selectionId: string | null;
}>({
  key: "DownloadManagerMergeSelectedFormatWithTypeSelector",
  get: ({ get }) => {
    const { formatsByTypes, selectionId } = get(downloadManagerFormatsQuery);
    const selectedFormats = get(downloadManagerSelectedFormatsByTypeState);
    const favoriteFormats = get(downloadManagerPreferenceFormatFavoritesState);
    const open = get(downloadManagerOpenState);
    const requestDownloadIsEnable = get(requestDownloadIsEnableSelector);

    const newFormatsByTypes: DownloadManagerFormatsByType[] = formatsByTypes.map(
      (formatsByType) => {
        const formats: FormatType[] = formatsByType.formats
          .map((format) => ({
            ...format,
            active: selectedFormats.length > 0 ? selectedFormats.includes(format.id) : false,
            favorite: favoriteFormats.length > 0 ? favoriteFormats.includes(format.id) : false,
          }))
          .sort((a, b) => {
            if (a.favorite === b.favorite) {
              return 0;
            } else if (a.favorite && !b.favorite) {
              return -1;
            }
            return 1;
          });
        // If we don't have any format available, add the download request card if it's enabled in corresponding tech pro and if it's enabled in admin
        if (
          // TODO: This first condition should evolve to not look into the length but look for the download rights (no dwl/SD/HD)

          // Add download request selection if not in HD definition
          requestDownloadIsEnable &&
          formatsByType.id !== RICH_TYPE_META_MODEL_ID &&
          ((open && formatsByType.definition && formatsByType.definition !== Definition.HD) ||
            formats.length === 0)
        ) {
          // Already pass the length for when we will make a difference between no formats and SD/MD/HD Formats
          const formatTypeAsk = formatTypeAskDownload(
            formats.length,
            selectedFormats.some((f) => f === `${formatsByType.type}_${formatTypeIdAskDownload}`),
            `${formatsByType.type}_${formatTypeIdAskDownload}`,
          );

          formats.push(formatTypeAsk);
        }
        return { ...formatsByType, formats };
      },
    );

    // Custom out
    const { formatsByTypes: formatsByTypesOut } = customSelectorInOut(
      FunctionOverrideKey.DownloadManagerFormatsOut,
      {
        formatsByTypes: newFormatsByTypes,
      },
      get(customerCommonOverrideAtom),
    );

    return { formatsByTypes: formatsByTypesOut, selectionId };
  },
});

export const downloadManagerMergeSelectedFormatWithElementSelector = selector<
  DownloadManagerFormatsByElement[]
>({
  key: "DownloadManagerMergeSelectedFormatWithElementSelector",
  get: ({ get }) => {
    const { formatsByTypes } = get(downloadManagerFormatsQuery);
    const selectedFormats = get(downloadManagerSelectedFormatsByElementState);
    const favoriteFormats = get(downloadManagerPreferenceFormatFavoritesState);
    // Convert from types to elements sorting
    const formatsByElements: DownloadManagerFormatsByElement[] = formatsByTypes.flatMap(
      (formatsByType) =>
        formatsByType.elements.map((element) => ({
          element,
          formats: formatsByType.formats,
          type: formatsByType.type,
        })),
    );

    // Merge with selected formats
    if (selectedFormats.length > 0 || favoriteFormats.length > 0) {
      const newFormatsByElements: DownloadManagerFormatsByElement[] = formatsByElements.map(
        (formatsByElement) => {
          const indexSelectedElement = selectedFormats.findIndex(
            (s) => s.element === formatsByElement.element,
          );
          const formats: FormatType[] = formatsByElement.formats
            .map((format) => ({
              ...format,
              active:
                indexSelectedElement !== -1 &&
                format.id === selectedFormats[indexSelectedElement].format,
              favorite: favoriteFormats.includes(format.id),
            }))
            .sort((a, b) => {
              if (a.favorite === b.favorite) {
                return 0;
              } else if (a.favorite && !b.favorite) {
                return -1;
              }
              return 1;
            });
          return { ...formatsByElement, formats };
        },
      );
      return newFormatsByElements;
    }

    return formatsByElements;
  },
});
